import React from "react";
import { motion } from "framer-motion";
import MascotOrb from "./MascotOrb";
import { useBookDemo } from "@/contexts/BookDemoContext";

export default function Hero() {
  const { openBookDemo } = useBookDemo();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-dvh flex items-center justify-center overflow-hidden pt-24 pb-20 text-white"
    >
      {/* ─── Dark gradient base ─── */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 120% 80% at 50% 0%, #1e1b4b 0%, transparent 60%)," +
            "radial-gradient(ellipse 80% 60% at 0% 100%, #0b1026 0%, transparent 70%)," +
            "radial-gradient(ellipse 80% 60% at 100% 100%, #0a1e2f 0%, transparent 70%)," +
            "linear-gradient(180deg, #05070f 0%, #0a0e20 50%, #0b0820 100%)",
        }}
      />

      {/* Slow-drifting mesh blobs (background depth only) */}
      <motion.div
        className="absolute -top-40 -left-40 w-[760px] h-[760px] rounded-full blur-3xl opacity-60"
        style={{ background: "radial-gradient(circle, rgba(59,130,246,0.85), transparent 65%)" }}
        animate={{ x: [0, 60, -30, 0], y: [0, 40, -20, 0], scale: [1, 1.08, 0.95, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -bottom-52 -right-52 w-[820px] h-[820px] rounded-full blur-3xl opacity-60"
        style={{ background: "radial-gradient(circle, rgba(139,92,246,0.85), transparent 65%)" }}
        animate={{ x: [0, -40, 60, 0], y: [0, -60, 20, 0], scale: [1, 1.1, 0.92, 1] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-[0%] right-[8%] w-[440px] h-[440px] rounded-full blur-3xl opacity-45"
        style={{ background: "radial-gradient(circle, rgba(236,72,153,0.55), transparent 65%)" }}
        animate={{ x: [0, 40, -30, 0], y: [0, 40, -30, 0], scale: [1, 1.1, 0.95, 1] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Subtle grid overlay for depth */}
      <div
        className="absolute inset-x-0 top-0 h-[820px] pointer-events-none opacity-[0.14]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.6) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(148,163,184,0.6) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />

      {/* Laser beams traveling along the grid (Tron-style) */}
      <div
        className="absolute inset-x-0 top-0 h-[820px] pointer-events-none overflow-hidden"
        aria-hidden
        data-testid="hero-grid-lasers"
        style={{
          maskImage:
            "radial-gradient(ellipse at center, black 25%, transparent 72%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 25%, transparent 72%)",
        }}
      >
        {/* Horizontal laser: 1px bright line sliding top → bottom */}
        <span
          className="grid-laser-h"
          style={{
            top: "144px", // snap to a grid row (72px * 2)
          }}
        />
        <span
          className="grid-laser-h"
          style={{
            top: "432px",
            animationDelay: "3.5s",
            animationDuration: "11s",
            opacity: 0.6,
          }}
        />

        {/* Vertical laser: 1px bright line sliding left → right */}
        <span
          className="grid-laser-v"
          style={{
            left: "36%",
          }}
        />
        <span
          className="grid-laser-v"
          style={{
            left: "72%",
            animationDelay: "4.2s",
            animationDuration: "13s",
            opacity: 0.55,
          }}
        />
      </div>

      {/* ─── Hero content ─── */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Mascot + subtle AI aura animation */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: -6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto mb-8 flex items-center justify-center"
          style={{ width: 360, height: 360 }}
          data-testid="hero-mascot"
        >
          {/* Breathing soft halo (outermost, very subtle) */}
          <span
            className="absolute rounded-full pointer-events-none hero-breath"
            style={{
              inset: 40,
              background:
                "radial-gradient(circle, rgba(129,140,248,0.45), rgba(139,92,246,0.0) 65%)",
            }}
          />

          {/* Slow conic gradient sweep — rotates around the mascot like an AI scan */}
          <span
            className="absolute rounded-full pointer-events-none hero-sweep"
            style={{
              inset: 80,
              background:
                "conic-gradient(from 0deg, rgba(167,139,250,0) 0deg, rgba(167,139,250,0) 220deg, rgba(244,114,182,0.55) 300deg, rgba(96,165,250,0.65) 345deg, rgba(129,140,248,0) 360deg)",
              maskImage:
                "radial-gradient(circle, transparent 46%, black 50%, black 56%, transparent 60%)",
              WebkitMaskImage:
                "radial-gradient(circle, transparent 46%, black 50%, black 56%, transparent 60%)",
              filter: "blur(0.5px)",
            }}
          />
          {/* Counter-rotating thinner secondary sweep */}
          <span
            className="absolute rounded-full pointer-events-none hero-sweep-rev"
            style={{
              inset: 60,
              background:
                "conic-gradient(from 0deg, rgba(34,211,238,0) 0deg, rgba(34,211,238,0) 260deg, rgba(34,211,238,0.45) 330deg, rgba(34,211,238,0) 360deg)",
              maskImage:
                "radial-gradient(circle, transparent 47%, black 50%, black 53%, transparent 56%)",
              WebkitMaskImage:
                "radial-gradient(circle, transparent 47%, black 50%, black 53%, transparent 56%)",
            }}
          />

          {/* 3 ultra-subtle sonar ripples */}
          <span className="sonar-ring absolute" style={{ inset: 100 }} />
          <span
            className="sonar-ring absolute"
            style={{ inset: 100, animationDelay: "1.3s" }}
          />
          <span
            className="sonar-ring absolute"
            style={{ inset: 100, animationDelay: "2.6s" }}
          />

          {/* Tight bright ring directly around mascot */}
          <span
            className="absolute rounded-full"
            style={{
              inset: 110,
              boxShadow:
                "0 0 54px 10px rgba(99,102,241,0.35), inset 0 0 32px 2px rgba(129,140,248,0.3)",
              border: "1px solid rgba(129,140,248,0.4)",
            }}
          />

          {/* Mascot */}
          <div className="relative z-10">
            <MascotOrb size={150} />
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="mt-6 text-balance font-extrabold tracking-[-0.035em] text-white leading-[0.94] text-[48px] sm:text-[76px] lg:text-[104px] xl:text-[120px]"
          data-testid="hero-headline"
          style={{ textShadow: "0 10px 50px rgba(0,0,0,0.45)" }}
        >
          Corporate Training,{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage:
                "linear-gradient(115deg, #60a5fa 0%, #a78bfa 45%, #f472b6 100%)",
            }}
          >
            Reimagined
          </span>
          <span className="text-white">.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-6 max-w-xl mx-auto text-[17px] sm:text-[19px] leading-relaxed text-slate-300"
          data-testid="hero-subtext"
        >
          A 1:1 AI trainer for every employee.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-3"
        >
          <button
            type="button"
            data-testid="hero-cta-primary"
            onClick={() => openBookDemo()}
            className="btn-sheen inline-flex items-center gap-2 text-[15px] font-semibold text-white px-7 py-4 rounded-full shadow-[0_18px_40px_-10px_rgba(99,102,241,0.85)] hover:-translate-y-0.5 transition-all"
            style={{
              background:
                "linear-gradient(135deg, #3b82f6 0%, #6366f1 50%, #a855f7 100%)",
            }}
          >
            Book a demo
            <span aria-hidden>→</span>
          </button>
          <a
            href="#how-it-works"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 text-[15px] font-semibold text-white bg-white/10 backdrop-blur-md border border-white/20 px-6 py-4 rounded-full hover:bg-white/15 hover:border-white/30 hover:-translate-y-0.5 transition-all"
          >
            <span
              className="flex items-center justify-center w-5 h-5 rounded-full"
              style={{
                background:
                  "linear-gradient(135deg, rgba(96,165,250,0.35) 0%, rgba(167,139,250,0.35) 100%)",
              }}
            >
              <span className="block w-0 h-0 border-l-[5px] border-l-white border-y-[3.5px] border-y-transparent ml-0.5" />
            </span>
            Watch how it works
          </a>
        </motion.div>
      </div>
    </section>
  );
}
